/**
 * ConsentFillSignPopup — Screen 3 of the document-centric consent feature (PROD-1051).
 *
 * Opens from Docs → Forms (no download). A right-side Sheet over the dimmed Docs surface:
 * the clinic-uploaded template PDF renders read-only with the on-file patient data
 * auto-filled at the mapped boxes, the provider signature + clinic stamp applied, and the
 * patient/guardian signs live in the dashed box. On Save it creates the consent draft and
 * signs it — the server fills + stamps + flattens + PAdES-seals the PDF and files it under
 * Docs → Forms (Type="Form"). Composes PdfBoxOverlay in `fill` mode + the SignaturePad pad.
 *
 * Wire contract: Scrips.Patient #883/#884 (merged). Browser-verify is deploy-gated (the
 * create→sign→Forms round-trip hits the live API once scrips.devops#54 unblocks the deploy).
 *
 * Free-text + static labels (PROD-1051, BE #888): two box flavours beyond data-bound text.
 *  - STATIC LABEL — the admin typed fixed copy in the builder; it rides on `FieldMapEntry.text`
 *    and the BE bakes it verbatim (renders as a read-only `value` box here).
 *  - FREE TEXT (fill-time) — an unbound box the signer types into; captured in `fieldInputs` and
 *    sent as `SignConsentBody.fieldValues`. The BE merges these ONLY for keys it didn't resolve
 *    server-side, so patient.* / signing.date / static labels stay authoritative.
 *
 * Flagged follow-ups (merged BE doesn't cover these yet — non-blocking, boxes degrade gracefully):
 *  - Provider-sig / clinic-stamp AUTO-SOURCE: pass `providerSignatureUrl` / `clinicStampUrl`
 *    (+ `providerName`) from the caller. Resolving them from the signed-in provider's
 *    ProfessionalDetails / Practice needs the session→doctorSetupId/practiceSetupId mapping
 *    + blob-CORS verified post-deploy; until then they're optional props (BE leaves the box
 *    blank if absent — non-fatal).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Download, Save, X } from 'lucide-react';
import { Banner, Button, PdfBoxOverlay, SignaturePad, type PdfOverlayBox, BrandLoading } from '@scripsteam/scrips-signal-ds';
import { usePatient } from '@/patients/services/use-patient';
import { useCreateConsent, useSignConsent, useProviderSignatureUrl, type PickableProvider } from '@/consent/services';
import { ProviderSignaturePicker } from './provider-signature-picker';
import { TopazPadCapture, SIGWEB_SCRIPT_URL } from './topaz-pad-capture';
import { isSigWebLoaded, loadSigWeb } from '@/consent/signature';
import { loadTemplatePdf, renderPageToCanvas, type PdfDocument } from '@/consent/lib/pdf';
import { buildConsentSigners, fillValueForKey, guardianOnFile, imageUrlToDataUrl } from '@/consent/lib/fill-values';
import { getBlob } from '@/shared/api/api-client';
import { toast } from 'sonner';
import type { ConsentTemplate } from '@/consent/types/consent-template-api';
import type { SignConsentBody } from '@/consent/types/consent-api';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

export interface ConsentFillSignPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  /** A document-mode template (carries `uploadedPdfUrl` + `fieldMap`). */
  template: ConsentTemplate;
  /** Links the consent to an encounter (appointment fill) → encounter-scoped Form doc.
   *  Omit for a patient-scoped (chart) consent. */
  encounterId?: string | null;
  actorId?: string | null;
  onSigned?: () => void;
  patientName?: string;
  mrn?: string;
  /** "By who's using it" — caller-supplied (signed-in provider + practice). Optional; flagged above. */
  providerSignatureUrl?: string | null;
  clinicStampUrl?: string | null;
  providerName?: string | null;
  /**
   * When the signed-in user is NOT a doctor (receptionist / nurse / admin),
   * they must pick which provider's signature to apply if the template needs
   * one. The popup renders a provider search and resolves the picked provider's
   * signature. When false (signed-in doctor), `providerSignatureUrl`/
   * `providerName` are used directly (their own), no picker. PROD-1051.
   */
  allowProviderPick?: boolean;
  /**
   * Loading flag for an AUTO-SOURCED provider signature (`allowProviderPick=false`,
   * e.g. the appointment's treating provider). Suppresses the "no signature on file"
   * error while that provider's professional details are still fetching, so the error
   * doesn't flash before the URL resolves. PROD-1243.
   */
  providerSignatureLoading?: boolean;
  /**
   * PROD-3195. Who the generic `signature` field defaults to: a minor's recording consent needs the
   * guardian on file, an adult's the patient. Only a default: the pad's role toggle still switches.
   * `guardian` applies only when a guardian is on file (a guardian signer without one is refused by
   * the server), otherwise the default stays the patient. Absent = patient, as before.
   */
  preferredSigner?: 'patient' | 'guardian';
}

// Page canvas width; the sheet's page frame is `w-120` (480px) to match.
const PAGE_W = 480;
const PATIENT_PAD_ID = 'patient-sig';
const GUARDIAN_PAD_ID = 'guardian-sig';
const SIGNATURE_PAD_ID = 'generic-sig'; // generic signature field — signer picks patient/guardian
const CAPABILITY = 'canvas';

// FHIR scope/category for a document-mode treatment consent (v1 default, confirmed by Samer
// 2026-06-17). `scope = treatment` is the FHIR-R4-correct scope for a "consent to be treated"
// (patient-privacy is the records-disclosure scope — used only for the HIE/data-sharing variant,
// NABIDH/Malaffi). Stored as FHIR-mirror metadata only (no effect on the document fill).
// FOLLOW-UP (PROD-1051, Samer): make scope/category PER-TEMPLATE once the real consent-type
// taxonomy (surgical, anaesthesia, telehealth, transfusion, media, HIE, …) is validated against
// partner-clinic forms — so clinical/PHI classification isn't a single hardcoded default.
const CREATE_DEFAULTS = {
  consentType: 'clinical' as const,
  scopeCode: 'treatment',
  categoryCode: '59284-0',
  categoryDisplay: 'Patient Consent',
  categorySystem: 'http://loinc.org',
};

type Page = { index: number; boxes: PdfOverlayBox[] };

export function ConsentFillSignPopup({
  open, onOpenChange, patientId, template, encounterId, actorId, onSigned,
  patientName, mrn, providerSignatureUrl, clinicStampUrl, providerName,
  allowProviderPick = false, providerSignatureLoading = false, preferredSigner,
}: ConsentFillSignPopupProps) {
  const { data: patient } = usePatient(open ? patientId : null);
  // PROD-2683. The guardian on file. A guardian signature counts on the server only when it names
  // this guardian (`signerReferenceId`), so with none on file the guardian box stays inert.
  const guardian = useMemo(() => guardianOnFile(patient), [patient]);
  const noGuardianOnFile = !!patient && !guardian;
  const create = useCreateConsent();
  const sign = useSignConsent(patientId);

  const [doc, setDoc] = useState<PdfDocument | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [patientSig, setPatientSig] = useState<string | null>(null);
  const [guardianSig, setGuardianSig] = useState<string | null>(null);
  const [fieldInputs, setFieldInputs] = useState<Record<string, string>>({}); // fill-time free-text, keyed by box id
  const [saving, setSaving] = useState(false);
  const [downloadingBlank, setDownloadingBlank] = useState(false);
  // Topaz pen-pad support (CG-F, PROD-1064). When a SigWeb service + pad are
  // present the signing modal offers "On pad"; otherwise the toggle is hidden
  // and the flow is on-screen canvas exactly as before. `signCapability`
  // records which surface actually produced the signature so the sign body /
  // FHIR Provenance carry the true method.
  const [padAvailable, setPadAvailable] = useState(false);
  const [signMethod, setSignMethod] = useState<'canvas' | 'topaz'>('canvas');
  const [signCapability, setSignCapability] = useState<'canvas' | 'topaz'>(CAPABILITY);
  // A GENERIC `signature` field was tapped → the pad modal shows a Patient/Guardian role toggle,
  // and only the chosen role is kept (a generic field has exactly one signer). The ref mirrors the
  // state so the onChange callback (stable identity) reads the live value.
  const [genericActive, setGenericActive] = useState(false);
  const genericRef = useRef(false);
  // Which signature box was tapped → reveals the rail SignaturePad for that signer. One pad,
  // shared by patient + guardian (same capture, switched by which box the user pressed).
  const [activeSigner, setActiveSigner] = useState<'patient' | 'guardian' | null>(null);
  const onFieldInput = useCallback((id: string, value: string) => {
    setFieldInputs((prev) => ({ ...prev, [id]: value }));
  }, []);

  // Provider signature resolution (PROD-1051). A signed-in doctor auto-signs
  // with their own signature (`providerSignatureUrl`/`providerName` props, no
  // picker). A non-doctor filler (`allowProviderPick`) must pick the treating
  // provider; we then fetch that provider's stored signature.
  const [pickedProvider, setPickedProvider] = useState<PickableProvider | null>(null);
  const needsProvider = useMemo(
    () => (template?.fieldMap ?? []).some((f) => f.type === 'provider-signature'),
    [template?.fieldMap],
  );
  const { signatureUrl: pickedSigUrl, isLoading: pickedSigLoading } =
    useProviderSignatureUrl(allowProviderPick ? (pickedProvider?.doctorSetupId ?? null) : null);
  const effProviderSigUrl = allowProviderPick ? pickedSigUrl : (providerSignatureUrl ?? null);
  const effProviderName = allowProviderPick ? (pickedProvider?.name ?? null) : (providerName ?? null);

  const canvasRefs = useRef<Map<number, HTMLCanvasElement>>(new Map());
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Load + render the uploaded template PDF when the popup opens. Loads via the BE stream
  // endpoint (same-origin/authed) — NOT the raw blob SAS, which the browser CORS-blocks.
  useEffect(() => {
    if (!open || !template?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const d = await loadTemplatePdf(template.id);
        if (cancelled) return;
        setDoc(d); setNumPages(d.numPages);
      } catch {
        if (!cancelled) { setDoc(null); setNumPages(0); }
      }
    })();
    return () => { cancelled = true; };
  }, [open, template?.id]);

  // Probe for a Topaz pad when the popup opens: load SigWebTablet.js (bounded by
  // loadSigWeb's onload/timeout gate — no unbounded wait) then check the globals
  // are PRESENT (`isSigWebLoaded`, a non-blocking typeof read). We deliberately
  // do NOT ping the SigWeb service here: on some builds that ping is a blocking
  // call, and a hung service must never stall the popup on open. The live
  // service check happens when capture starts (TopazPadCapture surfaces a
  // connection error). No SigWeb / non-Windows / no pad → `padAvailable` stays
  // false → the "On pad" option never renders. Never throws.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        await loadSigWeb(SIGWEB_SCRIPT_URL);
        if (!cancelled) setPadAvailable(isSigWebLoaded());
      } catch {
        if (!cancelled) setPadAvailable(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!doc) return;
    let cancelled = false;
    (async () => {
      for (let i = 1; i <= doc.numPages; i++) {
        const page = await doc.getPage(i);
        const canvas = canvasRefs.current.get(i - 1);
        if (canvas && !cancelled) await renderPageToCanvas(page, canvas, PAGE_W).catch(() => {});
      }
    })();
    return () => { cancelled = true; };
  }, [doc, numPages]);

  // Build the fill boxes from the template field map, grouped by page, with live values.
  const pages = useMemo<Page[]>(() => {
    const fieldMap = template?.fieldMap ?? [];
    const byPage = new Map<number, PdfOverlayBox[]>();
    for (let i = 0; i < fieldMap.length; i++) {
      const f = fieldMap[i];
      const id = `${f.type}-${i}`;
      const base = { id, x: f.xPct * 100, y: f.yPct * 100, w: f.wPct * 100, h: f.hPct * 100 };
      let box: PdfOverlayBox;
      if (f.type === 'text') {
        // Static label (f.text) and on-file data render read-only; an unresolved box becomes a
        // fill-time free-text input keyed by f.key (sent in fieldValues on save).
        const value = f.text || fillValueForKey(f.key, patient, effProviderName);
        box = value
          ? { ...base, type: 'text', boundKey: f.key, value, render: 'value' }
          : { ...base, id: f.key, type: 'text', boundKey: f.key, value: fieldInputs[f.key] ?? '',
              placeholder: f.key.includes('.') ? f.key.split('.').pop() : 'Type here', render: 'input' };
      } else if (f.type === 'provider-signature') {
        // Only show as applied when a provider signature is actually resolved
        // (signed-in doctor's, or a picked provider's) — otherwise a placeholder,
        // so the box doesn't look pre-signed the moment the form opens.
        box = effProviderSigUrl
          ? { ...base, type: 'provider-signature', sigName: effProviderName ?? undefined, render: 'applied-sig' }
          : { ...base, type: 'provider-signature', render: 'placeholder' };
      } else if (f.type === 'clinic-stamp') {
        box = clinicStampUrl
          ? { ...base, type: 'clinic-stamp', render: 'applied-stamp' }
          : { ...base, type: 'clinic-stamp', render: 'placeholder' };
      } else if (f.type === 'guardian-signature') {
        box = guardian
          ? { ...base, type: 'guardian-signature', label: 'Guardian — sign here', padId: GUARDIAN_PAD_ID, render: guardianSig ? 'signed' : 'pad-pending' }
          : { ...base, type: 'guardian-signature', label: 'No guardian on file', render: 'placeholder' };
      } else if (f.type === 'signature') {
        // Generic signature — renders as the patient-signature pad; the signer picks patient or
        // guardian in the modal. Signed once EITHER role is captured.
        box = { ...base, type: 'patient-signature', label: 'Sign here', padId: SIGNATURE_PAD_ID, render: (patientSig || guardianSig) ? 'signed' : 'pad-pending' };
      } else {
        box = { ...base, type: 'patient-signature', label: 'Sign here', padId: PATIENT_PAD_ID, render: patientSig ? 'signed' : 'pad-pending' };
      }
      const arr = byPage.get(f.page) ?? [];
      arr.push(box); byPage.set(f.page, arr);
    }
    return Array.from(byPage.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([index, boxes]) => ({ index, boxes }));
  }, [template?.fieldMap, patient, guardian, effProviderName, effProviderSigUrl, clinicStampUrl, patientSig, guardianSig, fieldInputs]);

  const hasGuardianBox = useMemo(
    () => (template?.fieldMap ?? []).some((f) => f.type === 'guardian-signature'),
    [template?.fieldMap],
  );
  const hasSigBox = useMemo(
    () => (template?.fieldMap ?? []).some(
      (f) => f.type === 'patient-signature' || f.type === 'guardian-signature' || f.type === 'signature',
    ),
    [template?.fieldMap],
  );
  // Tapping a signature field on the PDF opens the signing pad MODAL for that field; the box
  // reports its padId, so the captured signature drops into the exact field tapped. Because you
  // can't know in advance whether the adult patient or the guardian will sign, each signature
  // field is independently sign-by-tap — whoever is present signs the field that applies
  // (Andrew 2026-06-17).
  const onSigBoxTap = useCallback((boxId: string) => {
    // PdfBoxOverlay passes the BOX id (`${type}-${i}`), not padId — derive the field kind from it.
    const generic = boxId.startsWith('signature'); // generic `signature` box → role toggle
    // PROD-2683. No guardian on file → nobody can sign as guardian; the box does not open a pad.
    if (boxId.startsWith('guardian') && !guardian) return;
    genericRef.current = generic;
    setGenericActive(generic);
    // Generic field defaults to Patient (or the guardian on file when the host says a guardian is the
    // signer, PROD-3195); the modal toggle can switch.
    const genericDefault = preferredSigner === 'guardian' && guardian ? 'guardian' : 'patient';
    setActiveSigner(boxId.startsWith('guardian') ? 'guardian' : generic ? genericDefault : 'patient');
  }, [guardian, preferredSigner]);
  const onSigChange = useCallback((png: string | null) => {
    setActiveSigner((cur) => {
      if (cur === 'guardian') { setGuardianSig(png); if (genericRef.current) setPatientSig(null); }
      else { setPatientSig(png); if (genericRef.current) setGuardianSig(null); }
      return cur;
    });
  }, []);
  const activeSig = activeSigner === 'guardian' ? guardianSig : patientSig;

  // Save unlocks once ANY signature field is signed — an adult signs the patient field, a minor's
  // guardian signs the guardian field; either is a valid signing.
  const signed = !!patientSig || !!guardianSig;
  // When the template requires a provider signature, block Save until one is resolved
  // (a signed-in doctor's own, or a picked provider with a signature on file). PROD-1051.
  const providerOk = !needsProvider || !!effProviderSigUrl;
  const canSave = signed && providerOk && !saving && !!template?.id;

  // Download the BLANK form PDF so the patient can read it before signing.
  // Document-mode templates already have an uploaded blank PDF served at
  // `GET /api/ConsentTemplate/{id}/pdf` (same-origin, authed) — fetch it and
  // save it client-side. No signing/fill happens here.
  const handleDownloadBlank = useCallback(async () => {
    if (!template?.id || downloadingBlank) return;
    setDownloadingBlank(true);
    try {
      const blob = await getBlob(`/api/ConsentTemplate/${template.id}/pdf`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${template.name ?? 'consent-form'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download the form. Please try again.');
    } finally {
      setDownloadingBlank(false);
    }
  // Deps are the whole `template` (not `template?.id`/`.name`): PA runs the React
  // Compiler lint at error level and bails on manual memoization less specific than
  // its inference. `template` is the parent's stable state object, so this is identical
  // in practice and keeps the component optimizable.
  }, [template, downloadingBlank]);

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    try {
      // 1. Create the consent draft pinned to this template revision.
      const consentRecordId = await create.mutateAsync({
        patientId,
        encounterId: encounterId ?? null,
        consentTemplateId: template.id,
        templateVersionHash: template.versionHash,
        capabilityCode: signCapability,
        ...CREATE_DEFAULTS,
      });

      // 2. Resolve provider sig + clinic stamp images (optional; null degrades gracefully).
      const [providerSignaturePng, clinicStampPng] = await Promise.all([
        imageUrlToDataUrl(effProviderSigUrl),
        imageUrlToDataUrl(clinicStampUrl),
      ]);

      // 3. Sign — server fills + stamps + flattens + PAdES → Docs → Forms (Type="Form").
      // A guardian signer names the guardian on file (PROD-2683): the server matches it by id.
      const signers = buildConsentSigners({ patientSig, guardianSig, patientName, guardian, capabilityCode: signCapability });

      const body: SignConsentBody = {
        consentRecordId,
        capabilityCode: signCapability,
        actorId: actorId ?? null,
        signers,
        providerSignaturePng,
        clinicStampPng,
        providerName: effProviderName ?? null,
        fieldValues: Object.keys(fieldInputs).length ? fieldInputs : null,
      };
      await sign.mutateAsync(body);
      onSigned?.();
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  // Provider-signature step status. With a picker (non-doctor filler) it walks
  // select → loading → applied / no-signature-on-file; a signed-in doctor just
  // shows applied / not-on-file.
  const providerSub = effProviderSigUrl
    ? (allowProviderPick ? 'Applied' : 'Applied automatically')
    : allowProviderPick
      ? !pickedProvider
        ? (needsProvider ? 'Select a provider' : 'Optional')
        : pickedSigLoading
          ? 'Loading…'
          : 'No signature on file'
      : 'Not on file';
  const steps = [
    { n: 1, label: 'Provider signature', sub: providerSub, done: !!effProviderSigUrl },
    { n: 2, label: 'Clinic stamp', sub: clinicStampUrl ? 'Applied automatically' : 'Not on file', done: !!clinicStampUrl },
    { n: 3, label: 'Signature', sub: signed ? 'Captured' : 'Tap a signature field on the form to sign', done: signed, canClear: signed },
  ];

  // Portal to <body> so the fixed overlay escapes any transformed/`contain` ancestor (the chart
  // layout) and covers the FULL viewport — not just the Docs content area (Andrew 2026-06-17).
  return createPortal(
    <div dir="ltr" data-slot="consent-sheet-layer">
      {/* full-page backdrop tint over the whole app (the real page sits behind) */}
      <div aria-hidden data-slot="consent-sheet-scrim" />

      {/* right sheet (design PaConsentSheet; look: showcase-patient.css, section "consent sheet") */}
      <div data-slot="consent-sheet">
        <header data-slot="consent-sheet-header">
          <Button type="button" variant="secondary" icon={<X className="size-4" aria-hidden />} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="secondary"
            icon={<Download className="size-4" aria-hidden />}
            onClick={handleDownloadBlank}
            disabled={downloadingBlank || !template?.id}
            title="Download the blank form for the patient to read before signing"
          >
            {downloadingBlank ? 'Downloading…' : 'Download'}
          </Button>
          <div data-slot="consent-sheet-titles">
            <span data-slot="consent-sheet-title">Fill &amp; Sign · {template?.name ?? 'Consent'}</span>
            <span data-slot="consent-sheet-sub">{patientName ?? ''}{mrn ? ` · ${mrn}` : ''}</span>
          </div>
          <Button type="button" variant="primary" icon={<Save className="size-4" aria-hidden />} disabled={!canSave} onClick={handleSave}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </header>

        <div data-slot="consent-sheet-main">
          {/* PDF */}
          <div data-slot="consent-sheet-pdf">
            <div data-slot="consent-sheet-pdf-bar">
              <span data-slot="consent-sheet-pdf-name">{template?.name ?? 'consent'} · {numPages || '—'} page{numPages === 1 ? '' : 's'}</span>
              {patient && <span data-slot="consent-sheet-filled"><Check className="size-4" aria-hidden />Patient data auto-filled</span>}
            </div>
            <div data-slot="consent-sheet-pages" className="scrollbar-hide">
              {!doc && <BrandLoading size={48} label="Loading template…" />}
              {Array.from({ length: numPages }).map((_, i) => {
                const pg = pages.find((p) => p.index === i);
                return (
                  <div key={i} ref={(el) => { if (el) pageRefs.current.set(i, el); }} data-slot="consent-sheet-page">
                    <canvas ref={(el) => { if (el) canvasRefs.current.set(i, el); }} className="block w-full" />
                    {pg && (
                      <PdfBoxOverlay
                        boxes={pg.boxes} mode="fill"
                        onFieldInput={onFieldInput}
                        onPadDown={(id) => onSigBoxTap(id)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* signing rail */}
          <aside data-slot="consent-sheet-rail" className="scrollbar-hide">
            <div data-slot="consent-sheet-block" data-block="signing">
              <span data-slot="consent-sheet-eyebrow">Signing</span>
              {steps.map((s) => (
                <div key={s.n} data-slot="consent-sheet-step">
                  <span data-slot="consent-sheet-num" data-done={s.done ? 'true' : undefined}>
                    {s.done ? <Check className="size-4" aria-hidden /> : s.n}
                  </span>
                  <span data-slot="consent-sheet-step-text">
                    <span data-slot="consent-sheet-step-label">{s.label}</span>
                    <span data-slot="consent-sheet-step-sub">{s.sub}</span>
                    {s.canClear && <button type="button" data-slot="consent-sheet-clear" onClick={() => { setPatientSig(null); setGuardianSig(null); }}>Clear &amp; re-sign</button>}
                  </span>
                </div>
              ))}
            </div>

            {/* Provider picker — only when the form needs a provider signature AND the signed-in
                user isn't a doctor (a doctor auto-signs with their own). The filler selects the
                treating provider; Save is blocked until a provider with a signature is resolved. */}
            {needsProvider && allowProviderPick && (
              <div data-slot="consent-sheet-block">
                <span data-slot="consent-sheet-eyebrow">Provider signature</span>
                <ProviderSignaturePicker
                  value={pickedProvider?.doctorSetupId ?? null}
                  onSelect={setPickedProvider}
                />
                {!pickedProvider && (
                  <span data-slot="consent-sheet-note">Required — select the treating provider whose signature goes on this form.</span>
                )}
                {pickedProvider && !pickedSigLoading && !effProviderSigUrl && (
                  <Banner variant="warning" role="alert" title="No signature on file">{pickedProvider.name} has no signature on file — pick another provider, or add one in Settings → Professional Details.</Banner>
                )}
              </div>
            )}

            {/* PROD-2683. The form has a guardian signature, and no guardian is on file for this
                patient. Same blocking-error pattern as the provider signature below. */}
            {hasGuardianBox && noGuardianOnFile && (
              <div data-slot="consent-sheet-block">
                <span data-slot="consent-sheet-eyebrow">Guardian signature</span>
                <Banner variant="warning" role="alert" title="No guardian on file">
                  No guardian on file for this patient — add one in the patient's Personal details before a guardian can sign.
                </Banner>
              </div>
            )}

            {/* Auto-sourced provider (allowProviderPick=false, e.g. the appointment's
                treating provider): no picker — but if that provider has no signature
                on file, surface the SAME blocking error so the user knows why Save is
                disabled (PROD-1243). Suppressed while the signature is still loading. */}
            {needsProvider && !allowProviderPick && !providerSignatureLoading && !effProviderSigUrl && (
              <div data-slot="consent-sheet-block">
                <span data-slot="consent-sheet-eyebrow">Provider signature</span>
                <Banner variant="warning" role="alert" title="No signature on file">
                  {effProviderName ? `${effProviderName} has no signature on file` : 'The treating provider has no signature on file'} — add one in Settings → Professional Details. Save is blocked until then.
                </Banner>
              </div>
            )}

            <div data-slot="consent-sheet-status">
              <span data-slot="consent-sheet-eyebrow">Status</span>
              <div data-slot="consent-sheet-kv"><span>Form</span><b>{template?.name ?? 'Consent'}</b></div>
              <div data-slot="consent-sheet-kv"><span>Destination</span><b>Documents › Forms</b></div>
              <div data-slot="consent-sheet-kv"><span>Format</span><span data-slot="consent-sheet-mono">PDF · PAdES</span></div>
              <span data-slot="consent-sheet-ready" data-signed={signed ? 'true' : undefined}>
                {!hasSigBox ? 'This template has no signature field.' : signed ? 'Ready to save to Documents › Forms as a PAdES-sealed PDF.' : 'Tap a signature field on the form to sign — Save unlocks once signed.'}
              </span>
            </div>
          </aside>
        </div>
      </div>

      {/* Signing pad — opens when a signature field on the form is tapped; the drawn signature
          drops into THAT field (patient or guardian, whichever was tapped). Clear is built into
          the pad; Apply closes once a mark is present. */}
      {activeSigner && (
        <div data-slot="consent-pad-scrim">
          <div
            data-slot="consent-pad"
            role="dialog"
            aria-label={genericActive ? 'Signature' : activeSigner === 'guardian' ? 'Guardian signature' : 'Patient signature'}
          >
            <h3 data-slot="consent-pad-title">
              {genericActive ? 'Signature' : activeSigner === 'guardian' ? 'Guardian signature' : 'Patient signature'}
            </h3>
            {/* Generic field: just sign — no "who is signing" prompt (the patient's data is already
                captured on the form, Andrew 2026-06-17). */}
            {/* Method toggle — only when a Topaz pad is detected. Without SigWeb this
                never renders and the surface is the on-screen canvas as before. */}
            {padAvailable ? (
              <div data-slot="consent-pad-methods" role="radiogroup" aria-label="Signing method">
                {(['canvas', 'topaz'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    role="radio"
                    aria-checked={signMethod === m}
                    data-on={signMethod === m ? 'true' : undefined}
                    onClick={() => {
                      setSignMethod(m);
                      setSignCapability(m);
                      onSigChange(null); // switching surface clears the in-progress mark
                    }}
                  >
                    {m === 'canvas' ? 'Draw on screen' : 'Topaz pad'}
                  </button>
                ))}
              </div>
            ) : null}
            {padAvailable && signMethod === 'topaz' ? (
              <TopazPadCapture
                onCapture={(png) => {
                  setSignCapability('topaz');
                  onSigChange(png);
                }}
              />
            ) : (
              <SignaturePad
                label="Sign here"
                height={190}
                value={activeSig}
                onChange={(png) => {
                  setSignCapability('canvas');
                  onSigChange(png);
                }}
                clearable
              />
            )}
            <div data-slot="consent-pad-actions">
              <Button type="button" variant="primary" disabled={!activeSig} onClick={() => setActiveSigner(null)}>
                Apply
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}

export default ConsentFillSignPopup;
